import { Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CoverageType } from '../../../../../models/insuranceCoverage'
import { COVERAGE_TYPE_OPTIONS, type CoverageFormData, newCoverage } from './insuranceFormShared'

interface Props {
  value: CoverageFormData[]
  onChange: (next: CoverageFormData[]) => void
  /** Called with the id of an existing (persisted) coverage removed from the list. */
  onRemoveExisting?: (id: string) => void
  showPremiumPerUnit?: boolean
  /** On a policy: the carrier's number for the policy each line is written on. */
  showPolicyNumber?: boolean
  coverageOptions?: { value: CoverageType; label: string }[]
}

function isPhysicalDamage(coverage: CoverageFormData): boolean {
  return coverage.coverageType === CoverageType.AUTO_PHYSICAL_DAMAGE
}

/** Retyping a row drops the comprehensive deductible: only physical damage has one,
 *  and its input is hidden elsewhere, so a leftover value would still be saved. */
export function withCoverageField(
  coverage: CoverageFormData,
  field: keyof CoverageFormData,
  value: string | number | null
): CoverageFormData {
  const next = { ...coverage, [field]: value }
  return isPhysicalDamage(next) ? next : { ...next, comprehensiveDeductible: null }
}

export function CoveragesFieldset({
  value,
  onChange,
  onRemoveExisting,
  showPremiumPerUnit = true,
  showPolicyNumber = false,
  coverageOptions = COVERAGE_TYPE_OPTIONS,
}: Props) {
  const update = (index: number, field: keyof CoverageFormData, fieldValue: string | number | null) =>
    onChange(value.map((c, i) => (i === index ? withCoverageField(c, field, fieldValue) : c)))

  const add = () => onChange([...value, newCoverage()])

  const remove = (index: number) => {
    const coverage = value[index]
    if (coverage.id && !coverage.isNew) onRemoveExisting?.(coverage.id)
    onChange(value.filter((_, i) => i !== index))
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Coverages</h3>
        <Button type="button" variant="outline" size="sm" onClick={add}>
          <Plus className="mr-1 h-4 w-4" />
          Add coverage
        </Button>
      </div>
      <div className="space-y-4">
        {value.map((coverage, index) => (
          <div key={coverage.id ?? `new-${index}`} className="rounded-md border p-4">
            <div className="mb-3 flex items-center justify-between">
              <Select value={coverage.coverageType} onValueChange={(v) => update(index, 'coverageType', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {coverageOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label="Remove coverage"
                onClick={() => remove(index)}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div
              className={`grid grid-cols-2 items-end gap-3 ${showPremiumPerUnit ? 'sm:grid-cols-4' : 'sm:grid-cols-3'}`}
            >
              <div>
                <Label className="text-muted-foreground text-xs">Premium</Label>
                <div className="mt-1">
                  <CurrencyInput value={coverage.premium} onChange={(v) => update(index, 'premium', v)} />
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Limit</Label>
                <div className="mt-1">
                  <CurrencyInput value={coverage.limit} onChange={(v) => update(index, 'limit', v)} />
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">
                  {isPhysicalDamage(coverage) ? 'Collision deductible' : 'Deductible'}
                </Label>
                <div className="mt-1">
                  <CurrencyInput
                    value={coverage.deductible}
                    onChange={(v) => update(index, 'deductible', v)}
                  />
                </div>
              </div>
              {/* Physical damage is written comprehensive + collision, each with its own. */}
              {isPhysicalDamage(coverage) && (
                <div>
                  <Label className="text-muted-foreground text-xs">Comprehensive deductible</Label>
                  <div className="mt-1">
                    <CurrencyInput
                      value={coverage.comprehensiveDeductible}
                      onChange={(v) => update(index, 'comprehensiveDeductible', v)}
                    />
                  </div>
                </div>
              )}
              {showPremiumPerUnit && (
                <div>
                  <Label className="text-muted-foreground text-xs">Premium/unit</Label>
                  <div className="mt-1">
                    <CurrencyInput
                      value={coverage.premiumPerUnit}
                      onChange={(v) => update(index, 'premiumPerUnit', v)}
                    />
                  </div>
                </div>
              )}
            </div>
            {showPolicyNumber && (
              <div className="mt-3">
                <Label className="text-muted-foreground text-xs">Policy number</Label>
                <Input
                  className="mt-1"
                  value={coverage.vendorPolicyId}
                  onChange={(e) => update(index, 'vendorPolicyId', e.target.value)}
                  placeholder="The carrier's policy number for this line"
                  aria-label="Policy number"
                />
              </div>
            )}
          </div>
        ))}
        {value.length === 0 && (
          <p className="text-muted-foreground py-4 text-center text-sm">
            No coverages. Click &quot;Add coverage&quot; to add one.
          </p>
        )}
      </div>
    </div>
  )
}
